import React, { useEffect, useState, useMemo } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PieController,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useSelector } from 'react-redux';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PieController,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboardHome = () => {
  const [dashboardData, setDashboardData] = useState({
    aggregatedSales: {},
    salesByBranch: [],
    salesByCashier: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useSelector((state) => state.user);

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [aggregatedResponse, groupedResponse] = await Promise.all([
          fetch(`/api/sales/salesTimeByBranch?branch=${currentUser.branch}`),
          fetch(`/api/sales/salesBranchByBranch?branch=${currentUser.branch}`)
        ]);

        if (!aggregatedResponse.ok || !groupedResponse.ok) {
          throw new Error('Failed to fetch sales data');
        }

        const [aggregatedData, groupedData] = await Promise.all([
          aggregatedResponse.json(),
          groupedResponse.json()
        ]);

        setDashboardData({
          aggregatedSales: aggregatedData,
          salesByBranch: groupedData.salesByBranch,
          salesByCashier: groupedData.salesByCashier
        });
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
           
    fetchSalesData();
  }, [currentUser.branch, currentUser._id]);

  const formatNumber = useMemo(() => (num) => {
    if (!num) return '0';
    if (num < 1000) return num.toLocaleString();
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    return (num / 1000).toFixed(1) + 'K';
  }, []);

  const chartData = useMemo(() => ({
    bar: (data, label) => ({
      labels: data.map(item => item._id),
      datasets: [
        {
          label,
          data: data.map(item => item.total),
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(5, 38, 255, 1)',
          borderWidth: 1,
          hoverBackgroundColor: 'rgba(75, 192, 192, 0.4)',
          hoverBorderColor: 'rgba(75, 192, 192, 1)',
        },
      ],
    }),
    pie: (data) => ({
      labels: data.map(item => item._id),
      datasets: [
        {
          data: data.map(item => item.total),
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#FF9F40',
            '#4BC0C0',
          ],
          hoverBackgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#FF9F40',
            '#4BC0C0',
          ],
        },
      ],
    })
  }), []);

  if (loading) return <LoadingSpinner fullPage />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="tw-p-4 tw-max-w-7xl tw-mx-auto">
      
      {/* Summary Cards */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6 tw-mb-8">
        {['daily', 'weekly', 'monthly', 'yearly'].map((period) => (
          <div 
            key={period} 
            className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-text-center hover:tw-shadow-lg tw-transition-shadow"
          >
            <p className="tw-text-red-500 tw-font-bold tw-text-2xl tw-mb-2">
              ${formatNumber(dashboardData.aggregatedSales[`${period}Total`])}
            </p>
            <p className="tw-text-gray-600 tw-capitalize">
              {period.replace('ly', '')}ly sales
            </p>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
        <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6">
          <h2 className="tw-text-blue-800 tw-text-xl tw-font-semibold tw-mb-4">
            Sales by Branch
          </h2>
          <div className="tw-h-80">
            <Pie 
              data={chartData.pie(dashboardData.salesByBranch)} 
              options={{ 
                responsive: true, 
                maintainAspectRatio: false,
                plugins: { 
                  legend: { position: 'top' }, 
                  title: { display: false } 
                } 
              }}
            />
          </div>
        </div>

        <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6">
          <h2 className="tw-text-blue-800 tw-text-xl tw-font-semibold tw-mb-4">
            Sales by Cashier
          </h2>
          <div className="tw-h-80">
            <Bar 
              data={chartData.bar(dashboardData.salesByCashier, 'Sales by Cashier')} 
              options={{ 
                responsive: true, 
                maintainAspectRatio: false,
                plugins: { 
                  legend: { position: 'top' }, 
                  title: { display: false } 
                } 
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardHome;